<template>
    <div :class="plays.length > 0 ? 'p-0 table-card play-table-view' : ''">
        <div v-if="plays.length === 0" class="text-center py-5">
            <h5>No {{ status }} plays found</h5>
            <p>There are no {{ status }} plays to display.</p>
        </div>
        <table v-else class="table table-width-responsive table-with-pagination mb-0">
            <thead>
                <tr class="table-header-row">
                    <th @click="handleSort('name')" class="d-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Name
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'name' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'name' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th @click="handleSort('playbook_type')" class="d-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Type
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'playbook_type' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'playbook_type' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th class="d-none d-md-table-cell bg-white column-sort-disabled" style="width: 200px;">
                        <div class="d-flex flex-row">
                            Channels
                        </div>
                    </th>
                    <th @click="handleSort('created_by')" class="d-none d-lg-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Created By
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'created_by' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'created_by' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th @click="handleSort('created_at')" class="d-none d-xl-table-cell bg-white" style="cursor: pointer;">
                        <div class="d-flex flex-row">
                            Created At
                            <div class="d-flex flex-column">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-up.svg" :class="{'sort-key-active': sortKey === 'created_at' && sortOrder === 'asc'}" alt="">
                                <img class="sort-key" src="/img/icons/avid-icons/chevron-down.svg" :class="{'sort-key-active': sortKey === 'created_at' && sortOrder === 'des'}" alt="">
                            </div>
                        </div>
                    </th>
                    <th class="table-column-action column-sort-disabled d-table-cell bg-white pe-3" width="1"></th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(play, index) in plays" :key="play.id" :class="`table-row-${index + 1}`">
                    <td class="d-table-cell align-start text-start">
                        <a :href="`/playbooks/${client.url}/builder/${play.url}/strategy`" class="text-decoration-none">{{ play.name }}</a>
                    </td>
                    <td class="d-table-cell">
                        <span v-if="play.playbook_type">{{ play.playbook_type.name }}</span>
                        <span v-else class="text-muted">N/A</span>
                    </td>
                    <td class="d-none d-md-table-cell channels-column">
                        <div class="position-relative text-nowrap">
                            <template v-for="(asset_type, index) in getUniqueAssetTypes(play.asset_types)" :key="asset_type.id">
                                <span class="d-inline-block" data-bs-toggle="tooltip" :title="asset_type.name">
                                    <button class="btn btn-secondary btn-icon-only-sm" disabled style="pointer-events: none;" aria-label="Button">
                                        <img :src="'/img/icons/avid-icons/' + asset_type.icon_class + '.svg'" class="asset-type-icon icon" alt=""></img>
                                    </button>
                                </span>
                            </template>
                        </div>
                    </td>
                    <td class="d-none d-lg-table-cell">
                        <div class="user-info" v-if="play.created_by_user">
                            <img class="avatar rounded-circle d-none d-xl-inline" :src="play.created_by_user.image" />
                            {{ play.created_by_user.first_name }} {{ play.created_by_user.last_name ? play.created_by_user.last_name.charAt(0) + '.' : '' }}
                        </div>
                        <span v-else class="text-muted">Unknown</span>
                    </td>
                    <td class="d-none d-xl-table-cell">
                        <div class="text-muted">
                            {{ formatDate(play.created_at) }}
                        </div>
                    </td>
                    <td class="column-sort-disabled d-table-cell">
                        <div class="dropdown d-flex justify-content-center">
                            <button v-if="canEdit('playbooks')" class="btn btn-white btn-icon-only-md" data-bs-toggle="dropdown" data-bs-auto-close="true" aria-expanded="false" aria-label="Button">
                                <img class="icon" src="/img/icons/avid-icons/dots-vertical.svg" data-bs-toggle="tooltip" title="Actions" alt="">
                            </button>
                            <div class="dropdown-menu dropdown-menu-primary dropdown-menu-end">
                                <a class="dropdown-item" @click="openPlaybookDescription(play)" href="#">
                                    <img class="icon me-2" src="/img/icons/avid-icons/deep-dive.svg" alt=""> Play Overview
                                </a>
                                <a class="dropdown-item" :href="`/playbooks/${client.url}/builder/${play.url}/strategy`">
                                    <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit Play
                                </a>
                                <a class="dropdown-item" href="#" @click.prevent="copyPlay(play)">
                                    <img class="icon me-2" src="/img/icons/avid-icons/document-copy.svg" alt=""> Copy Play
                                </a>
                                <hr class="my-1" />
                                <a class="dropdown-item text-danger" href="#" @click.prevent="deletePlay(play)">
                                    <img class="icon red-icon me-2" src="/img/icons/avid-icons/trash.svg" alt=""> Delete Play
                                </a>
                            </div>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>

        <!-- Playbook Description Modal -->
        <playbook-type-information-modal 
            v-if="selectedPlaybook"
            :playbook_type="selectedPlaybook"
            :show="showPlaybookDescriptionModal"
            @update:show="showPlaybookDescriptionModal = $event"
        />
    </div>
</template>

<script>
    import { defineComponent } from 'vue';
    import vSelect from "vue3-select";
    import permissionsMixin from '../../mixins/permissions';
    import { usePlaybookStore } from '../../stores/playbook';
    import { useClientStore } from '../../stores/client';
    import { useNotifications } from '../../composables/useNotifications';
    
    export default defineComponent({
        name: 'PlayListView',
        emits: ['copy-play', 'sort-by-key', 'play-deleted'],
        props: {
            plays: {
                type: Array,
                required: true
            },
            status: {
                type: String,
                required: true
            },
            sortKey: {
                type: String,
                default: 'name'
            },
            sortOrder: {
                type: String,
                default: 'asc'
            }
        },
        components: { vSelect },
        mixins: [permissionsMixin],
        setup() {
            const playbookStore = usePlaybookStore();
            const clientStore = useClientStore();
            const { showNotification } = useNotifications();
            return {
                playbookStore,
                clientStore,
                showNotification
            };
        },
        computed: {
            client() {
                return this.clientStore.getClient;
            }
        },
        data() {
            return {
                // Modal state
                selectedPlaybook: null,
                showPlaybookDescriptionModal: false
            };
        },
        mounted() {
            // Initialize tooltips
            const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl));

            // Add event listeners to hide tooltips on click and mouseleave
            for (var i = 0; i < tooltipTriggerList.length; i++) {
              (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                  tooltipList[index].hide();
                });
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                  tooltipList[index].hide();
                });
              })(i);
            }
        },
        methods: {
            handleSort(key) {
                this.$emit('sort-by-key', key);
            },
            getUniqueAssetTypes(asset_types) {
                if (!asset_types) return [];
                const uniqueTypes = new Map();
                asset_types.forEach(type => {
                    if (!uniqueTypes.has(type.id)) {
                        uniqueTypes.set(type.id, type);
                    }
                });
                return Array.from(uniqueTypes.values());
            },
            formatDate(dateString) {
                if (!dateString) return 'Unknown';
                const date = new Date(dateString);
                return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: 'numeric', minute:'2-digit'});
            },
            deletePlay(playbook) {
                var self = this;

                Swal.fire({
                    title: 'Delete Play',
                    text: 'Are you sure you want to delete this play "' + playbook.name + '" ? This will remove the play and all associated assets from the system. This data cannot be restored.',
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: "Yes, delete",
                    customClass: {
                            popup: 'swal-warning'
                    },
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.axios.delete('/api/playbooks/'+self.client.url +'/'+ playbook.url)
                            .then(() => {
                                self.showNotification({
                                    text: '<strong>' + playbook.name + '</strong> was deleted!'
                                });
                                
                                // Emit event so parent can refresh pagination instead of full reload
                                self.$emit('play-deleted', playbook);
                            });
                    }
                });
            },
            copyPlay(playbook) {
                // Emit event to parent to handle the copy modal
                this.$emit('copy-play', playbook);
            },
            openPlaybookDescription(playbook) {
                // Pass the playbook_type from the play object, not the entire play object
                this.selectedPlaybook = playbook.playbook_type || playbook;
                this.showPlaybookDescriptionModal = true;
            }
        }
    });
</script>